import React, { useEffect, useRef, useState } from "react";
import Loader from "../../components/loader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_TYPE_ADHD,
  MODEL_TYPE_BIPOLAR_DISORDER,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import { useTranslation } from "react-i18next";
import {
  getBipolarDisorderImage,
  getCookie,
  getEnneagramBannerImage,
  toCamelCase,
} from "../../utils/utility-functions";
import { LOCALE, APP_NAVIGATION } from "../../utils/constants";
import Footer from "../../components/footer";
import ExpandableSection from "../../components/expandable-collapsible/component";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import PathHelper from "../../utils/path-helper";

const BipolarDisorderReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
}) => {
  const navigate = useNavigate();

  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const { t } = useTranslation();
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const bipolardisorder1 = "bipolar-1";
  const bipolardisorder2 = "bipolar-2";
  const bipolardisorder3 = "bipolar-3";
  const bipolardisorder4 = "bipolar-4";
  const bipolardisorderImage1 = getBipolarDisorderImage(bipolardisorder1);
  const bipolardisorderImage2 = getBipolarDisorderImage(bipolardisorder2);
  const bipolardisorderImage3 = getBipolarDisorderImage(bipolardisorder3);
  const bipolardisorderImage4 = getBipolarDisorderImage(bipolardisorder4);
  console.log("hello", assessmentAttemptId, getInsightfulContentConnect);
  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_BIPOLAR_DISORDER[currentLocale] ||
            MODEL_TYPE_BIPOLAR_DISORDER[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          console.log("Bipolar Disorder Content Data:", content);
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId, currentLocale]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId]);

  // Get background color based on depression level

  const bannerImage = getEnneagramBannerImage();
  const disclaimer = contentData?.disclaimer;

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };
  const HeroBanner = ({ title, color = "purple" }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent z-5`}
        />
        <div className="absolute inset-0 bg-black opacity-10 z-5" />

        <div className="relative h-full flex items-start">
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12">
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span>{firstWord}</span> <span>{restOfTitle}</span>
                  </span>
                ) : (
                  title
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const CallToActionSection = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.colorblindness.personality_test_cta")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.discover_more_about_your_personality_type")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} mb-8 flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p
            className={`text-lg ${textClr || "text-gray-700"} mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]"></div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2
                            className={`text-lg font-semibold ${
                              textClr || "text-gray-700"
                            }`}
                          >
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p
                          className={`${
                            textClr || "text-gray-700"
                          } text-base md:text-lg leading-relaxed relative`}
                        >
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p
                        className={`${
                          textClr || "text-gray-700"
                        } text-base md:text-lg leading-relaxed`}
                      >
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    className = "",
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full ${className}`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };

  // Function to calculate user's bipolar disorder score
  const calculateBipolarScore = () => {
    if (!userTraits || userTraits.length === 0) return 0;

    // Calculate average score from all traits
    const totalScore = userTraits.reduce(
      (sum, trait) => sum + (trait.score || 0),
      0
    );
    const averageScore = totalScore / userTraits.length;

    return averageScore;
  };

  // Function to get result category based on score
  const getResultCategory = (score) => {
    if (score >= 1 && score <= 2.19) {
      return "VeryLowLikelihood";
    } else if (score >= 2.2 && score <= 3.39) {
      return "LowLikelihood";
    } else if (score >= 3.4 && score <= 4.59) {
      return "ModerateLikelihood";
    } else if (score >= 4.6 && score <= 5.79) {
      return "HighLikelihood";
    } else if (score >= 5.8 && score <= 7) {
      return "VeryHighLikelihood";
    }
  };

  // Get current user's score and category
  const userScore = calculateBipolarScore();
  const resultCategory = getResultCategory(userScore);

  // Get the result data based on category - data is directly in contentData, not under Results
  const resultData = contentData?.[resultCategory] || null;

  // Get background color based on category
  const getBackgroundColor = (category) => {
    switch (category) {
      case "VeryLowLikelihood":
        return "bg-green-200";
      case "LowLikelihood":
        return "bg-yellow-200";
      case "ModerateLikelihood":
        return "bg-orange-200";
      case "HighLikelihood":
        return "bg-red-200";
      case "VeryHighLikelihood":
        return "bg-red-500";
    }
  };

  const categoryBgColor = getBackgroundColor(resultCategory);

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData.heading} color="blue" />
                  }
                >
                  {token && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                          {t("report.bipolar_disorder.introduction")}
                        </h2>
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          {contentData.intro}
                        </p>
                      </div>
                      <div className="flex items-end ">
                        <img
                          src={bipolardisorderImage1}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain mt-4"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.resultsHeading?.replace(/:$/, "")}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                      {/* Top-left - Main Result Card */}
                      <DescriptiveCard
                        heading={resultData?.Heading}
                        overview={resultData?.FlavorText}
                        bgClr={categoryBgColor}
                        textClr={"text--grey-700"}
                      />
                      <div className="flex flex-col items-center md:justify-center space-y-6">
                        {/* Image */}
                        <img
                          src={bipolardisorderImage2}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain mt-10"
                        />
                      </div>
                      {/* Top-right - Section 1 */}
                      <div className="flex flex-col mt-5">
                        {resultData?.Section1?.Heading && (
                          <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            {resultData.Section1.Heading}
                          </h2>
                        )}
                        {resultData?.Section1?.FlavorText && (
                          <p className="text-lg text-gray-700 mb-6 md:min-h-[72px]">
                            {resultData.Section1.FlavorText}
                          </p>
                        )}
                        <InfoListCard
                          contentArray={resultData?.Section1?.Content}
                          bgClr={"#E0F2FE"}
                        />
                      </div>
                      {/* Bottom-left - Section 2 */}
                      <div className="flex flex-col mt-5">
                        {resultData?.Section2?.Heading && (
                          <h2 className="text-xl font-semibold text-gray-800 mb-3">
                            {resultData.Section2.Heading}
                          </h2>
                        )}
                        {resultData?.Section2?.FlavorText && (
                          <p className="text-lg text-gray-700 mb-6 md:min-h-[72px]">
                            {resultData.Section2.FlavorText}
                          </p>
                        )}
                        <InfoListCard
                          contentArray={resultData?.Section2?.Content}
                          bgClr={"#E0F2FE"}
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.SeekingHelp?.Heading}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                          {contentData?.SeekingHelp?.FlavorText}
                        </h2>

                        <InfoListCard
                          character={"i"}
                          heading={""}
                          overview={""}
                          contentArray={contentData?.SeekingHelp?.Content}
                          bgClr={"#E0F2FE"}
                        />
                      </div>
                      <div className="flex items-end justify-center h-full ">
                        <img
                          src={bipolardisorderImage3}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.NextSteps?.Heading}
                      color="blue"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[65%_35%] gap-4 items-start mb-5">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        {contentData?.NextSteps?.Content}
                      </div>
                      <div className="flex items-end ">
                        <img
                          src={bipolardisorderImage4}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain h-40 "
                        />
                      </div>
                    </div>
                    <DescriptiveCard
                      heading={contentData?.Disclaimer?.Heading}
                      overview={contentData?.Disclaimer?.FlavorText}
                      bgClr={"bg-orange-50"}
                      textClr={"text-orange-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>

              <CallToActionSection data={"contentData"} />
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BipolarDisorderReport;
